// web/src/routes/Replay.tsx
// Task 32: Replay route — fetches a saved roast from Firestore and rehydrates
// it into <RoastPlayer>.  View count is incremented best-effort; the error is
// swallowed so playback is never blocked by a transient increment failure.
import { useEffect, useRef, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { loadRoast, incrementViewCount } from "@/lib/roasts";
import { RoastPlayer } from "@/player/RoastPlayer";
import type { RoastArtifact } from "@/types/roast";
import { isPaged } from "@/types/roast";

export default function Replay() {
  const { id } = useParams<{ id: string }>();
  const [artifact, setArtifact] = useState<RoastArtifact | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "missing">("loading");
  const counted = useRef(false);

  useEffect(() => {
    if (!id) { setState("missing"); return; }
    let alive = true;
    loadRoast(id)
      .then((a) => {
        if (!alive) return;
        if (!a) { setState("missing"); return; }
        setArtifact(a);
        setState("ready");
        if (!counted.current) {
          counted.current = true;
          // Best-effort: swallow any increment error so replay is never blocked.
          incrementViewCount(id).catch((e) =>
            console.error("viewCount increment failed:", e),
          );
        }
      })
      .catch(() => { if (alive) setState("missing"); });
    return () => { alive = false; };
  }, [id]);

  if (state === "loading") {
    return <div className="grid h-screen place-items-center">Loading…</div>;
  }
  if (state === "missing" || !artifact) {
    return <Navigate to="/404" replace />;
  }

  const isDeckArtifact = isPaged(artifact);

  return (
    <div className="share-page h-screen w-screen">
      <RoastPlayer
        mode={artifact.mode}
        script={artifact.script}
        audioUrl={artifact.audioUrl ?? ""}
        imageUrl={isDeckArtifact ? null : artifact.imageUrl}
        wordTimings={artifact.wordTimings}
        detectedObjects={isDeckArtifact ? [] : artifact.detectedObjects}
        isCharacterFullscreen={artifact.isCharacterFullscreen}
        doodleImageUrl={isDeckArtifact ? undefined : (artifact.doodleImageUrl ?? undefined)}
        aspect={artifact.aspect}
        audio={artifact.audio}
        captionMask={artifact.captionMask}
        bleepMode={artifact.bleepMode}
        {...(isDeckArtifact
          ? { keySlides: artifact.keySlides, fillerImages: artifact.fillerImages, slideSchedule: artifact.slideSchedule }
          : {})}
      />
    </div>
  );
}
